/**
 * Reader for the CIRCAD v6 keyword format.
 *
 * The same format is used for design files (.PCB, .SCH) and for the keyword
 * style libraries (PCB.LIB, SCH.LIB) — a library is simply a document whose
 * entities happen to all be top-level components. One reader covers both.
 *
 * Field names and ordering follow the original writer in OG7.cpp:2000-3180,
 * which is the only authoritative specification of this format.
 */

import {
  type ArcEntity, type CadDocument, type ComponentEntity, type DocumentKind,
  type Entity, type FillEntity, type ImageRef, type Layer, type LineEntity, type PadEntity,
  type LayerType, type PadType, type SpiralEntity, type TextAnchor,
  type TextEntity, type UnitsSetting, emptyDocument, newId, padIsThrough,
} from '../model.ts';
import { relativiseComponent } from '../geom/transform.ts';
import { inferInnerLayers } from '../geom/layers.ts';
import { parseAngle, parseLength } from '../units.ts';
import {
  type CadRecord, field, fieldAll, parseColor, parseRecordLine,
  splitList, splitPoints, unquote,
} from './record.ts';

export interface ParseWarning {
  line: number;
  message: string;
}

export interface ParseResult {
  document: CadDocument;
  warnings: ParseWarning[];
}

const HEADER_RE = /^Contents:\s*(\S+)\s+Version\s+([0-9.]+)\s+Data File/i;

/** True if `text` is the keyword ("Contents:") flavour rather than compact. */
export function isKeywordFormat(text: string): boolean {
  return HEADER_RE.test(text.slice(0, 200)) || /^Layer::\s*Type:/m.test(text);
}

/**
 * Parse a v6 keyword document.
 *
 * `text` must already be decoded from Windows-1252 — the format uses 0xB0 for
 * the degree sign and 0xB5 for micro, and reading it as UTF-8 corrupts both.
 */
export function parseKeywordDocument(text: string, name = 'Untitled'): ParseResult {
  const warnings: ParseWarning[] = [];
  const lines = text.split(/\r?\n/);

  // Header block, terminated by the first record line.
  let kind: DocumentKind = 'pcb';
  const source: NonNullable<CadDocument['source']> = { version: '6.0', program: 'CIRCAD' };
  let i = 0;
  for (; i < lines.length; i++) {
    const l = lines[i]!;
    if (/^[A-Za-z][A-Za-z0-9]*::/.test(l)) break;
    const h = HEADER_RE.exec(l);
    if (h) { source.program = h[1]!; source.version = h[2]!; continue; }
    const fn = /^File Name:\s*(.*)$/.exec(l);
    if (fn) { source.fileName = fn[1]!.trim(); continue; }
    const ft = /^File Type:\s*(.*)$/i.exec(l);
    if (ft) { kind = /schematic/i.test(ft[1]!) ? 'sch' : 'pcb'; continue; }
    const fd = /^File Date:\s*(.*)$/i.exec(l);
    if (fd) { source.fileDate = fd[1]!.trim(); continue; }
    const ftm = /^File Time:\s*(.*)$/i.exec(l);
    if (ftm) { source.fileTime = ftm[1]!.trim(); continue; }
  }

  const doc = emptyDocument(kind, name);
  doc.source = source;
  // Layers come from the file, not from our defaults.
  doc.layers = [];
  doc.entities = [];
  const unknown: string[] = [];

  // Component nesting: entities are appended to the innermost open component.
  let openComponent: ComponentEntity | null = null;
  /** The Fill that FillMore:: records continue. */
  let openFill: FillEntity | null = null;

  const emit = (e: Entity) => {
    if (openComponent) openComponent.children.push(e);
    else doc.entities.push(e);
  };

  for (; i < lines.length; i++) {
    const raw = lines[i]!;
    if (/^END OF FILE/i.test(raw)) break;

    const rec = parseRecordLine(raw, i + 1);
    if (!rec) { openFill = null; continue; }
    if (rec.keyword !== 'FillMore') openFill = null;

    try {
      switch (rec.keyword) {
        // --- Document settings -------------------------------------------
        case 'Units': doc.units = readUnits(rec); break;
        case 'ZeroPoint':
          doc.zeroPoint = {
            x: len(rec, 'X', doc.units) ?? 0,
            y: len(rec, 'Y', doc.units) ?? 0,
          };
          break;
        case 'Gridsnaps':
          doc.grid = {
            current: len(rec, 'Current', doc.units) ?? 0.025,
            index: int(rec, 'Index') ?? 0,
            table: splitList(field(rec, 'Table')).map((t) => parseLength(t, doc.units)),
          };
          break;
        case 'Layer': doc.layers.push(readLayer(rec, doc.layers.length)); break;
        case 'Display': doc.display = readDisplay(rec, doc.units); break;
        case 'Print': {
          const b = splitPoints(field(rec, 'Bounds') ?? '');
          const b1 = b[0] ?? [];
          const b2 = b[1] ?? [];
          doc.print = {
            flags: splitList(field(rec, 'Flags')).map(Number),
            scaling: parseFloat(field(rec, 'Scaling') ?? '1') || 1,
            bounds: {
              x1: parseLength(b1[0] ?? '0', doc.units),
              y1: parseLength(b1[1] ?? '0', doc.units),
              x2: parseLength(b2[0] ?? '0', doc.units),
              y2: parseLength(b2[1] ?? '0', doc.units),
            },
            transparencyColor: parseColor(field(rec, 'TransparencyColor'))?.rgb,
            highlightColor: parseColor(field(rec, 'HighlightColor'))?.rgb,
          };
          break;
        }
        case 'NetScan':
          doc.netScan = {
            lineToLine: len(rec, 'LineToLine', doc.units) ?? 0,
            lineToPad: len(rec, 'LineToPad', doc.units) ?? 0,
            padToPad: len(rec, 'PadToPad', doc.units) ?? 0,
            ratLine: len(rec, 'RatLine', doc.units) ?? 0,
          };
          break;
        case 'FloodFill':
          doc.floodFill = {
            floodToLine: len(rec, 'FloodToLine', doc.units) ?? 0,
            floodToPad: len(rec, 'FloodToPad', doc.units) ?? 0,
            floodLine: len(rec, 'FloodLine', doc.units) ?? 0,
            hatchGrid: len(rec, 'HatchGrid', doc.units) ?? 0,
            hotSignal: unquote(field(rec, 'HotSignal')),
            coldSignal: unquote(field(rec, 'ColdSignal')),
            targetLayer: int(rec, 'TargetLayer') ?? 0,
            sourceLayers: splitList(field(rec, 'SourceLayers')).map(Number),
          };
          break;
        case 'GerberOut':
          doc.gerberOut = {
            maskPadSwell: len(rec, 'MaskPadSwell', doc.units) ?? 0,
            stencilPadShrink: len(rec, 'StencilPadShrink', doc.units) ?? 0,
            planePadSwell: len(rec, 'PlanePadSwell', doc.units) ?? 0,
            planeHoleSwell: len(rec, 'PlaneHoleSwell', doc.units) ?? 0,
            floodLineWidth: len(rec, 'FloodLineWidth', doc.units) ?? 0,
          };
          break;
        case 'GerberFile':
          (doc.gerberFiles ??= []).push({
            sequence: int(rec, 'Sequence') ?? 0,
            type: int(rec, 'Type') ?? 0,
            suffix: unquote(field(rec, 'Suffix')) ?? '',
            layers: splitList(field(rec, 'Layers')).map(Number),
          });
          break;
        case 'AutoRoute':
          doc.autoRoute = {
            algorithms: int(rec, 'Algorithms') ?? 0,
            layers: splitList(field(rec, 'Layers')).map(Number),
          };
          break;

        // --- Default primitive parameters --------------------------------
        case 'DefaultLine':
          doc.defaults.line = {
            layer: layerRef(rec), width: len(rec, 'Width', doc.units) ?? 0.01,
            style: int(rec, 'Style') ?? 0,
          };
          break;
        case 'DefaultFill':
          doc.defaults.fill = {
            layer: layerRef(rec), width: len(rec, 'Width', doc.units) ?? 0,
            style: int(rec, 'Style') ?? 0,
          };
          break;
        case 'DefaultArc':
          doc.defaults.arc = { layer: layerRef(rec) };
          break;
        case 'DefaultPad':
          doc.defaults.pad = {
            type: padType(field(rec, 'Type')) ?? 'T1',
            size: len(rec, 'Size', doc.units) ?? 0.05,
            hole: len(rec, 'Hole', doc.units),
            pin: unquote(field(rec, 'Pin')),
          };
          break;
        case 'DefaultText':
          doc.defaults.text = {
            layer: layerRef(rec), height: len(rec, 'Height', doc.units) ?? 0.06,
          };
          break;
        case 'DefaultVia':
          doc.defaults.via = {
            layer: layerRef(rec), type: padType(field(rec, 'Type')) ?? 'T3',
            size: len(rec, 'Size', doc.units) ?? 0.04,
            hole: len(rec, 'Hole', doc.units),
          };
          break;
        case 'DefaultXYDimension':
        case 'PlaceXYDimension':
          doc.defaults.xyDimension = {
            size: len(rec, 'Size', doc.units) ?? 0, keyword: rec.keyword,
          };
          break;

        // --- Geometry ------------------------------------------------------
        case 'Line': emit(readLine(rec, doc)); break;
        case 'Fill': { const f = readFill(rec, doc); openFill = f; emit(f); break; }
        case 'FillMore':
          if (openFill) appendVertices(openFill, rec, doc.units);
          else warnings.push({ line: rec.line, message: 'FillMore:: with no preceding Fill::' });
          break;
        case 'Arc': emit(readArc(rec, doc)); break;
        case 'Spiral': emit(readSpiral(rec, doc)); break;
        case 'Pad': emit(readPad(rec, doc)); break;
        case 'Text': emit(readText(rec, doc)); break;

        // A photograph placed under the drawing to trace from. It is named,
        // not embedded, and is kept apart from the entities so that nothing
        // which walks the drawing — Gerber, netlist, drill, isolation — can
        // ever pick one up.
        case 'PixelArray':
        case 'Bitmap':
          (doc.images ??= []).push(readImage(rec, doc));
          break;

        // --- Components ----------------------------------------------------
        case 'Component': {
          if (openComponent) {
            warnings.push({ line: rec.line, message: 'Component:: inside an unclosed component' });
            doc.entities.push(openComponent);
          }
          openComponent = readComponent(rec, doc);
          break;
        }
        case 'EndComp': {
          if (!openComponent) {
            warnings.push({ line: rec.line, message: 'EndComp:: with no open component' });
            break;
          }
          const ip = field(rec, 'InsertionPoint');
          if (ip) {
            const pts = splitPoints(ip)[0];
            if (pts) {
              openComponent.x = parseLength(pts[0] ?? '0', doc.units);
              openComponent.y = parseLength(pts[1] ?? '0', doc.units);
            }
          }
          // Children arrive in absolute coordinates, sharing the insertion
          // point's space. Rebase them so every component in memory is
          // origin-relative regardless of where it was loaded from.
          relativiseComponent(
            openComponent.children, openComponent.bounds,
            openComponent.x, openComponent.y, openComponent.rotation ?? 0,
          );
          doc.entities.push(openComponent);
          openComponent = null;
          break;
        }

        default:
          unknown.push(raw);
          break;
      }
    } catch (err) {
      warnings.push({
        line: rec.line,
        message: `${rec.keyword}:: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  }

  if (openComponent) {
    warnings.push({ line: lines.length, message: 'unterminated Component:: at end of file' });
    doc.entities.push(openComponent);
  }
  if (unknown.length) doc.unknown = unknown;
  if (!doc.layers.length) doc.layers = emptyDocument(kind).layers;
  // The file cannot say which copper is buried; work it out from the stack.
  inferInnerLayers(doc.layers);

  return { document: doc, warnings };
}

// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------

function len(rec: CadRecord, key: string, units: UnitsSetting): number | undefined {
  const v = field(rec, key);
  return v === undefined ? undefined : parseLength(v, units);
}

function int(rec: CadRecord, key: string): number | undefined {
  const v = field(rec, key);
  if (v === undefined) return undefined;
  const n = parseInt(v, 10);
  return Number.isNaN(n) ? undefined : n;
}

function ang(rec: CadRecord, key: string): number | undefined {
  const v = field(rec, key);
  return v === undefined ? undefined : parseAngle(v);
}

/**
 * An arc/spiral angle in degrees, from a field written in turns (1 = 360°). A
 * value carrying a ° suffix is taken as degrees, so files our own writer
 * produced still read back correctly.
 */
function turns(rec: CadRecord, key: string, def: number): number {
  const v = field(rec, key);
  if (v === undefined) return def;
  return parseTurns(v, def);
}

/**
 * One written angle, in degrees, by the original's turns-or-degrees rule.
 *
 * Exported because the writer has to agree with it exactly: it keeps the file's
 * own spelling of an angle when re-writing it unchanged, and it can only tell
 * "unchanged" by reading it back the same way this did.
 */
export function parseTurns(v: string, def = 0): number {
  const t = v.trim();
  // An explicit unit means degrees. (The original also accepts radians and
  // grads here; no file in the wild uses them.)
  if (/[°dD]/.test(t)) return parseAngle(t);

  const m = /^([+-]?(?:\d+\.?\d*|\.\d+))(?:\/([+-]?(?:\d+\.?\d*|\.\d+)))?/.exec(t);
  if (!m) return def;
  const value = parseFloat(m[1]!);
  if (!Number.isFinite(value)) return def;

  // The original's rule (OG7.cpp:4404): with no unit, a value is a fraction of
  // a turn when it is at most 1 *or* when it was written as a fraction —
  // `Sweep:1/2` is half a turn and `Rotation:64/256` a quarter, however large
  // the numerator looks. Only a bare number greater than one is degrees.
  if (m[2] !== undefined) {
    const denominator = parseFloat(m[2]);
    return (denominator ? value / denominator : value) * 360;
  }
  return value <= 1 ? value * 360 : value;
}

/** A `Layer:` reference is either a quoted name or a number. */
function layerRef(rec: CadRecord): number | string | undefined {
  const v = field(rec, 'Layer');
  if (v === undefined) return undefined;
  const t = v.trim();
  if (t.startsWith('"')) return unquote(t)!;
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? t : n;
}

/** Resolve a layer reference against the document's layer table. */
function resolveLayer(doc: CadDocument, ref: number | string | undefined): number {
  if (ref === undefined) return 0;
  if (typeof ref === 'number') return ref;
  const byName = doc.layers.findIndex((l) => l.name === ref);
  return byName >= 0 ? byName : 0;
}

function layerOf(rec: CadRecord, doc: CadDocument): number {
  return resolveLayer(doc, layerRef(rec));
}

function padType(v: string | undefined): PadType | undefined {
  if (!v) return undefined;
  const m = /^([TStS])\s*([0-3])/i.exec(v.trim());
  if (!m) return undefined;
  return `${m[1]!.toUpperCase()}${m[2]!}` as PadType;
}

/** Fields shared by every geometric primitive. */
function common(rec: CadRecord, doc: CadDocument) {
  const c = parseColor(field(rec, 'Color'));
  const out: {
    layer: number; color?: number; signal?: string; data?: string; origin?: number;
  } = { layer: layerOf(rec, doc) };
  if (c) out.color = c.rgb;
  const sig = unquote(field(rec, 'Signal'));
  if (sig) out.signal = sig;
  const data = field(rec, 'Data');
  if (data !== undefined) out.data = data;
  const origin = int(rec, 'Origin');
  if (origin !== undefined) out.origin = origin;
  return out;
}

// ---------------------------------------------------------------------------
// Record readers
// ---------------------------------------------------------------------------

function readUnits(rec: CadRecord): UnitsSetting {
  const def = field(rec, 'Default');
  const table = splitList(field(rec, 'Table')).map((t) => {
    const m = /^([+-]?(?:\d+\.?\d*|\.\d+))\s*(.*)$/.exec(t);
    return { scale: m ? parseFloat(m[1]!) : 1, name: (m?.[2] ?? '').trim() };
  });
  return {
    // `Default:1.000"` — the trailing marker makes this inches per base unit.
    default: def ? parseFloat(def.replace(/[^0-9.eE+-]/g, '')) || 1 : 1,
    table,
  };
}

function readLayer(rec: CadRecord, num: number): Layer {
  const screen = parseColor(field(rec, 'Screen'));
  const print = parseColor(field(rec, 'Print'));
  const rawType = int(rec, 'Type') ?? 0;
  return {
    num,
    name: unquote(field(rec, 'Name')) ?? `Layer ${num}`,
    // Low 3 bits are the type; bit 3 is the top/bottom flag.
    type: (rawType & 7) as LayerType,
    bottom: (rawType & 8) !== 0,
    screen: screen?.rgb ?? 0x888888,
    print: print?.rgb ?? 0x000000,
    screenNegative: screen?.negative ?? false,
    printNegative: print?.negative ?? false,
    visible: true,
    locked: false,
  };
}

function readDisplay(rec: CadRecord, units: UnitsSetting): CadDocument['display'] {
  const hex = (k: string) => parseColor(field(rec, k))?.rgb;
  const layer = field(rec, 'Layer');
  return {
    layer: layer?.startsWith('"') ? unquote(layer)! : parseInt(layer ?? '0', 10) || 0,
    drawModes: parseInt(field(rec, 'DrawModes') ?? '0', 16),
    dotSpacing: parseLength(field(rec, 'DotSpacing') ?? '0', units),
    dotColor: hex('DotColor') ?? 0xffffff,
    backgroundColor: hex('BackgroundColor') ?? 0x000000,
    highlightColor: hex('HighlightColor') ?? 0xffffff,
    attentionColor: hex('AttentionColor') ?? 0xffff00,
  };
}

function readLine(rec: CadRecord, doc: CadDocument): LineEntity {
  const pts = splitPoints(field(rec, 'Vertices') ?? '');
  const p1 = pts[0] ?? [];
  const p2 = pts[1] ?? [];
  const e: LineEntity = {
    kind: 'line',
    id: newId(),
    ...common(rec, doc),
    width: len(rec, 'Width', doc.units) ?? doc.defaults.line?.width ?? 0.01,
    x1: parseLength(p1[0] ?? '0', doc.units),
    y1: parseLength(p1[1] ?? '0', doc.units),
    x2: parseLength(p2[0] ?? p1[0] ?? '0', doc.units),
    y2: parseLength(p2[1] ?? p1[1] ?? '0', doc.units),
  };
  const bulge = ang(rec, 'Bulge');
  if (bulge) e.bulge = bulge;
  const style = int(rec, 'Style');
  if (style) e.style = style;
  const cost = field(rec, 'Cost');
  if (cost !== undefined) e.cost = parseFloat(cost);
  return e;
}

function readFill(rec: CadRecord, doc: CadDocument): FillEntity {
  const b = splitPoints(field(rec, 'Bounds') ?? '');
  const b1 = b[0] ?? [];
  const b2 = b[1] ?? [];
  const e: FillEntity = {
    kind: 'fill',
    id: newId(),
    ...common(rec, doc),
    width: len(rec, 'Width', doc.units) ?? 0,
    bounds: {
      x1: parseLength(b1[0] ?? '0', doc.units),
      y1: parseLength(b1[1] ?? '0', doc.units),
      x2: parseLength(b2[0] ?? '0', doc.units),
      y2: parseLength(b2[1] ?? '0', doc.units),
    },
    vertices: [],
  };
  const style = int(rec, 'Style');
  if (style) e.style = style;
  appendVertices(e, rec, doc.units);
  return e;
}

/** Append the `Vertices:` of a Fill:: or FillMore:: record. */
function appendVertices(fill: FillEntity, rec: CadRecord, units: UnitsSetting): void {
  for (const v of fieldAll(rec, 'Vertices')) {
    for (const p of splitPoints(v)) {
      fill.vertices.push({
        x: parseLength(p[0] ?? '0', units),
        y: parseLength(p[1] ?? '0', units),
      });
    }
  }
}

function readArc(rec: CadRecord, doc: CadDocument): ArcEntity {
  const c = splitPoints(field(rec, 'Center') ?? '')[0] ?? [];
  const e: ArcEntity = {
    kind: 'arc',
    id: newId(),
    ...common(rec, doc),
    width: len(rec, 'Width', doc.units) ?? 0.01,
    cx: parseLength(c[0] ?? '0', doc.units),
    cy: parseLength(c[1] ?? '0', doc.units),
    radius: len(rec, 'Radius', doc.units) ?? 0,
    // An arc's Angle/Sweep are written in *turns* (1 = 360°), not degrees —
    // unlike Rotation, which carries a ° suffix. A bare number is a turn; a
    // value with ° (e.g. from our own writer, historically) is degrees.
    angle: turns(rec, 'Angle', 0),
    // The writer omits Angle/Sweep entirely when sweep is zero, which is how a
    // full circle is stored (OG7.cpp:2174).
    sweep: turns(rec, 'Sweep', 360),
  };
  const ecc = field(rec, 'Eccentricity');
  if (ecc) e.eccentricity = parseFloat(ecc);
  // Rotation is turns-or-degrees like Angle/Sweep: `.75` is three-quarters of a
  // turn (270°), `90°` is degrees. Reading it as plain degrees left arcs a
  // fraction of a degree off true and their orientation wrong.
  const rot = turns(rec, 'Rotation', 0);
  if (rot) e.rotation = rot;
  const type = int(rec, 'Type');
  if (type) e.type = type;
  return e;
}

function readSpiral(rec: CadRecord, doc: CadDocument): SpiralEntity {
  const c = splitPoints(field(rec, 'Center') ?? '')[0] ?? [];
  return {
    kind: 'spiral',
    id: newId(),
    ...common(rec, doc),
    width: len(rec, 'Width', doc.units) ?? 0.01,
    cx: parseLength(c[0] ?? '0', doc.units),
    cy: parseLength(c[1] ?? '0', doc.units),
    r1: len(rec, 'StartRadius', doc.units) ?? len(rec, 'Radius', doc.units) ?? 0,
    r2: len(rec, 'StopRadius', doc.units) ?? 0,
    a1: ang(rec, 'Angle') ?? 0,
    a2: ang(rec, 'Sweep') ?? 0,
  };
}

function readPad(rec: CadRecord, doc: CadDocument): PadEntity {
  const c = splitPoints(field(rec, 'Center') ?? '')[0] ?? [];
  const type = padType(field(rec, 'Type')) ?? doc.defaults.pad?.type ?? 'T1';

  // `Size:` is either one value (square/round) or `x,y` for a rectangle. The
  // writer emits the second component with a bare comma (OG7.cpp:2325).
  const sizeRaw = field(rec, 'Size') ?? '';
  const sizeParts = sizeRaw.split(',').map((s) => s.trim()).filter(Boolean);
  const sizeX = sizeParts[0] !== undefined
    ? parseLength(sizeParts[0], doc.units)
    : doc.defaults.pad?.size ?? 0.05;
  const sizeY = sizeParts[1] !== undefined ? parseLength(sizeParts[1], doc.units) : sizeX;

  const e: PadEntity = {
    kind: 'pad',
    id: newId(),
    ...common(rec, doc),
    type,
    x: parseLength(c[0] ?? '0', doc.units),
    y: parseLength(c[1] ?? '0', doc.units),
    sizeX,
    sizeY,
  };
  // The drill falls back to `DefaultPad::`, as the type and size above already
  // do. A `Pad::` record that omits `Hole:` means "the default drill", not "no
  // drill" — and a through pad with no drill is a pad the board house never
  // makes a hole for, and that this program draws solid.
  const hole = len(rec, 'Hole', doc.units)
    ?? (padIsThrough(type) ? doc.defaults.pad?.hole : undefined);
  if (hole) e.hole = hole;
  const rot = turns(rec, 'Rotation', 0);
  if (rot) e.rotation = rot;
  const pin = unquote(field(rec, 'Pin'));
  if (pin !== undefined && pin !== '') e.pin = pin;

  readPadMarker(e);
  return e;
}

/**
 * Pad facts the v6 format has no field for, carried at the front of the
 * free-text payload as `{tent=both,nopaste,mask=0.00210}`.
 *
 * Read only from the front, and only when every part of it is understood — so
 * a payload that merely happens to begin with braces is left alone and handed
 * back as the user's own text, which it is.
 */
function readPadMarker(e: PadEntity): void {
  const marker = /^\{([^}]*)\}/.exec(e.data ?? '');
  if (!marker) return;

  const found: Partial<PadEntity> = {};
  for (const bit of marker[1]!.split(',')) {
    const tent = /^tent=(top|bottom|both)$/.exec(bit);
    if (tent) { found.tented = tent[1] as PadEntity['tented']; continue; }
    if (bit === 'nopaste') { found.noPaste = true; continue; }
    const mask = /^mask=(-?[\d.]+)$/.exec(bit);
    if (mask && Number.isFinite(Number(mask[1]))) {
      found.maskMargin = Number(mask[1]);
      continue;
    }
    // `\d`, not `d`. Without the backslash this matched the letter d and a
    // stop, so no real number ever parsed — and because an unrecognised bit
    // abandons the whole marker, a pad with a paste margin lost its tenting,
    // its mask margin and its slot along with it, and handed the reader the
    // raw `{…}` back as if the user had typed it.
    const paste = /^paste=(-?[\d.]+)$/.exec(bit);
    if (paste && Number.isFinite(Number(paste[1]))) {
      found.pasteMargin = Number(paste[1]);
      continue;
    }
    const slot = /^slot=(-?[\d.]+):(-?[\d.]+)$/.exec(bit);
    if (slot && Number.isFinite(Number(slot[1])) && Number.isFinite(Number(slot[2]))) {
      found.slot = { x: Number(slot[1]), y: Number(slot[2]) };
      continue;
    }
    return;   // not ours after all
  }

  Object.assign(e, found);
  const rest = (e.data ?? '').slice(marker[0].length);
  if (rest === '') delete e.data;
  else e.data = rest;
}

/**
 * CIRCAD's text-anchor grid (`txap[16]` in OG7.cpp), index = row*4 + col.
 * Rows are vertical (bottom, middle, top, origin), columns horizontal (left,
 * centre, right, origin); a bare letter means the centre of the other axis, and
 * "origin" (`O`) is the insertion line itself — baseline vertically, left
 * horizontally.
 */
const TXAP = [
  'BL', 'B', 'BR', 'BO',
  'L', 'C', 'R', 'CO',
  'TL', 'T', 'TR', 'TO',
  'OL', 'OC', 'OR', 'O',
];

/**
 * Map a CIRCAD `Anchor:` code to one of our nine anchor points.
 *
 * The port flags rely on this — `Anchor:R` grows the label leftward and keeps it
 * vertically centred in the flag rather than riding its top edge. CIRCAD's grid
 * is finer than ours (it distinguishes bottom from baseline), so both the bottom
 * and origin rows collapse to our baseline, and the origin column to left.
 */
export function parseAnchor(raw: string | undefined): TextAnchor | undefined {
  if (!raw) return undefined;
  const s = raw.trim().toUpperCase();
  // The reader takes a digit as the grid index itself (`ppt.F->prl2 |= getnum`,
  // OGfdi-cad00.cpp:1113), and the shipped port blocks are written that way —
  // `Anchor:4` on the signal flag, `Anchor:14` on the bus port. Read as a name
  // those found nothing and the label fell back to bottom-left, which put the
  // flag's own name outside the flag.
  const idx = /^\d+$/.test(s)
    ? Number(s) & 15
    : TXAP.indexOf(s);
  if (idx < 0) return undefined;
  const row = idx >> 2;
  const col = idx & 3;
  const v = row === 1 ? 'M' : row === 2 ? 'T' : 'B'; // bottom & origin -> baseline
  const h = col === 1 ? 'C' : col === 2 ? 'R' : 'L'; // origin -> left
  return `${v}${h}` as TextAnchor;
}

function readText(rec: CadRecord, doc: CadDocument): TextEntity {
  const ip = splitPoints(field(rec, 'InsertionPoint') ?? '')[0] ?? [];
  const e: TextEntity = {
    kind: 'text',
    id: newId(),
    ...common(rec, doc),
    x: parseLength(ip[0] ?? '0', doc.units),
    y: parseLength(ip[1] ?? '0', doc.units),
    height: len(rec, 'Height', doc.units) ?? doc.defaults.text?.height ?? 0.06,
    text: field(rec, 'Data') ?? '',
  };
  const length = len(rec, 'Length', doc.units);
  if (length !== undefined) e.length = length;
  const lines = int(rec, 'Lines');
  if (lines) e.lines = lines;
  const rawAnchor = field(rec, 'Anchor')?.trim();
  const anchor = parseAnchor(rawAnchor);
  if (anchor) e.anchor = anchor;
  if (rawAnchor) e.anchorRaw = rawAnchor;
  const font = field(rec, 'Font');
  if (font !== undefined) {
    const uq = unquote(font)!;
    e.font = /^\d+$/.test(uq) ? parseInt(uq, 10) : uq;
  }
  const style = int(rec, 'Style');
  if (style) e.style = style;
  const aspect = field(rec, 'Aspect');
  if (aspect) e.aspect = parseFloat(aspect);
  const arc = len(rec, 'Arc', doc.units);
  if (arc) e.arc = arc;
  const rawRot = field(rec, 'Rotation');
  const rot = turns(rec, 'Rotation', 0);
  if (rot) e.rotation = rot;
  // Keep the raw code: CIRCAD reads a bare angle <=1 as turns, so `Rotation:1`
  // is a full turn (0°), not 1°. Preserving the string lets a load→save leave
  // it untouched instead of rewriting it as `1°` (a genuine 1° tilt in CIRCAD).
  if (rawRot !== undefined && rot) e.rotationRaw = rawRot.trim();
  return e;
}


/**
 * `PixelArray:: Layer: InsertionPoint: Anchor: Width: Height: Resolution: Name:`
 *
 * Resolution is a distance per pixel, and the format allows it as a fraction —
 * `1/300` for a three-hundred-dot scan — which is why it is read with the same
 * helper the arcs use for their turns-or-degrees fields.
 */
function readImage(rec: CadRecord, doc: CadDocument): ImageRef {
  const p = splitPoints(field(rec, 'InsertionPoint') ?? '')[0] ?? [];
  const img: ImageRef = {
    name: unquote(field(rec, 'Name')) ?? '',
    x: parseLength(p[0] ?? '0', doc.units),
    y: parseLength(p[1] ?? '0', doc.units),
    width: int(rec, 'Width') ?? 0,
    height: int(rec, 'Height') ?? 0,
    resolution: len(rec, 'Resolution', doc.units) ?? 0,
  };
  // Only written when the two axes differ, so a square picture stays square.
  const ry = len(rec, 'ResolutionY', doc.units);
  if (ry && ry !== img.resolution) img.resolutionY = ry;
  const rot = turns(rec, 'Rotation', 0);
  if (rot) img.rotation = rot;
  // Always set, including zero: the pad master is a real answer here, and a
  // falsy test would drop it and change where the picture sits in the stack.
  img.layer = layerOf(rec, doc);
  const anchor = field(rec, 'Anchor');
  if (anchor) img.anchor = parseAnchor(anchor);
  return img;
}
function readComponent(rec: CadRecord, doc: CadDocument): ComponentEntity {
  const b = splitPoints(field(rec, 'Bounds') ?? '');
  const b1 = b[0] ?? [];
  const b2 = b[1] ?? [];

  const params: Record<number, string> = {};
  for (const f of rec.fields) {
    const m = /^P([1-9])$/.exec(f.key);
    if (m) params[parseInt(m[1]!, 10)] = unquote(f.value) ?? '';
  }

  const e: ComponentEntity = {
    kind: 'component',
    id: newId(),
    layer: layerOf(rec, doc),
    name: unquote(field(rec, 'Name')) ?? '',
    bounds: {
      x1: parseLength(b1[0] ?? '0', doc.units),
      y1: parseLength(b1[1] ?? '0', doc.units),
      x2: parseLength(b2[0] ?? '0', doc.units),
      y2: parseLength(b2[1] ?? '0', doc.units),
    },
    x: 0,
    y: 0,
    params,
    children: [],
  };
  const flags = int(rec, 'Flags');
  if (flags) e.flags = flags;
  const rot = turns(rec, 'Rotation', 0);
  if (rot) e.rotation = rot;
  return e;
}
